import { useLocation } from "react-router";

import { Box } from "@mui/material";

import ConsumerHomePage from "@/src/pages/consumer/HomePage";
import {
  createHomePageStructuredData,
  getHomePageSeoDescription,
  getHomePageSeoTitle
} from "@/src/pages/consumer/HomePage/HomePageStructuredData";

import { PublicRoute } from "@/src/auth/components/PublicRoute";
import { VehicleType } from "@/src/types/types";
import { toCanonicalUrl } from "@/src/utils/siteUrl";
import { normalizePathname } from "@/src/utils/weBuyCarsLandingPages";

import { loadHomeRouteData, loadHomeRouteDataOnClient } from "../homeRouteData";
import type { Route } from "./+types/home";

const PATH_TO_VEHICLE_TYPE: Record<string, VehicleType> = {
  "/": VehicleType.CAR,
  "/moto": VehicleType.MOTO,
  "/caravan": VehicleType.CARAVAN
};

// Runs at build time (ssr:false + prerender) so the static HTML carries the
// dealer logos and the newest articles; images still load lazily.
export async function loader() {
  return loadHomeRouteData();
}

// Client-side navigations reuse the prerendered data when available. No
// `hydrate = true`, so the initial render uses the inlined build-time data.
export async function clientLoader({ serverLoader }: Route.ClientLoaderArgs) {
  try {
    return (await serverLoader()) as Awaited<ReturnType<typeof loader>>;
  } catch {
    return loadHomeRouteDataOnClient();
  }
}

export const meta: Route.MetaFunction = ({ location }) => {
  const pathname = normalizePathname(location.pathname);
  const vehicleType = PATH_TO_VEHICLE_TYPE[pathname] ?? VehicleType.CAR;
  const title = getHomePageSeoTitle(vehicleType);
  const description = getHomePageSeoDescription(vehicleType);

  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    {
      property: "og:image",
      content:
        "https://baana.nettiauto.com/assets/images/baana-by-nettiauto-logo-red.png"
    },
    { property: "og:type", content: "website" },
    {
      tagName: "link",
      rel: "canonical",
      href: toCanonicalUrl(pathname)
    },
    { "script:ld+json": createHomePageStructuredData(vehicleType) }
  ];
};

// No mount animation (e.g. MUI `Fade`) and no auth spinner: the prerendered
// HTML and the first client render must both contain the real content.
export default function HomeRoute({ loaderData }: Route.ComponentProps) {
  const { pathname } = useLocation();
  const vehicleType =
    PATH_TO_VEHICLE_TYPE[normalizePathname(pathname)] ?? VehicleType.CAR;

  return (
    <PublicRoute routeType="consumer" renderWhileAuthenticating>
      <Box sx={{ display: "flex", flex: "1" }}>
        <ConsumerHomePage
          vehicleType={vehicleType}
          dealerLogos={loaderData.dealerLogos}
          initialArticles={loaderData.articles}
        />
      </Box>
    </PublicRoute>
  );
}
